import React, { useState, useEffect, useCallback } from "react";
import { Cctv, Search, X, Eye, Play, Loader2, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import baseapi from "../../services/baseapi";
import { type LocationIds } from "../../components/LocationSelectorSection";
import LiveStreamModal, { type CameraType } from "../Assets/LiveStreamModal";
import { jwt_decode } from "../../utils/jwt_decode";
import { showError } from "../../utils/toast";

const formatDate = (dateString: string) => {
    if (!dateString || dateString === '-') return '-';
    try {
        const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
        const date = new Date(normalized);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
        });
    } catch { return dateString; }
};

interface CameraListSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    initialStatus: "all" | "active" | "offline";
    locationIds: LocationIds;
    level: any;
}

export const CameraListSidebar: React.FC<CameraListSidebarProps> = ({
    isOpen,
    onClose,
    initialStatus,
    locationIds,
    level
}) => {
    const [cameraListStatus, setCameraListStatus] = useState<"all" | "active" | "offline">(initialStatus);
    const [cameraListLoading, setCameraListLoading] = useState(false);
    const [cameraList, setCameraList] = useState<any[]>([]);
    const [cameraListSearch, setCameraListSearch] = useState("");
    const [cameraListPage, setCameraListPage] = useState(1);
    const [cameraListLimit] = useState(10);
    const [cameraListTotal, setCameraListTotal] = useState(0);

    const [liveStreamModalOpen, setLiveStreamModalOpen] = useState(false);
    const [liveStreamCamera, setLiveStreamCamera] = useState<CameraType | null>(null);
    const [lastFrameModalOpen, setLastFrameModalOpen] = useState(false);
    const [lastFrameImage, setLastFrameImage] = useState<string | null>(null);
    const [lastFrameLoading, setLastFrameLoading] = useState(false);
    const [lastFrameAssetName, setLastFrameAssetName] = useState<string>('');

    const fetchLastFrame = async (clientId: number, assetId: number, assetName: string) => {
        try {
            setLastFrameLoading(true);
            setLastFrameModalOpen(true);
            setLastFrameImage(null);
            setLastFrameAssetName(assetName);
            const res = await baseapi.get('/camera_frames', {
                params: { query: JSON.stringify({ client_id: clientId, asset_id: assetId }) }
            });
            if (res.data.status && res.data.data && res.data.data.length > 0) {
                setLastFrameImage(res.data.data[0].last_frame);
            } else {
                setLastFrameImage(null);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch camera frame');
            setLastFrameImage(null);
        } finally {
            setLastFrameLoading(false);
        }
    };

    const handleOpenLiveStream = (item: any) => {
        try {
            const jwt = jwt_decode();
            const tenant_name = jwt.tenant_id || jwt.tenant_name;
            const org_id = jwt.organization_id;

            const streamPath = tenant_name
                ? `${tenant_name}-${item.organization_id || org_id}-${item.client_id}-${item.agent_id}_${item.id}`
                : `${item.organization_id || org_id}-${item.client_id}-${item.agent_id}_${item.id}`;

            const cameraObj: CameraType = {
                id: String(item.id),
                name: item.name,
                status: item.status?.toLowerCase() === 'active' || item.status?.toLowerCase() === 'online' ? 'online' : 'offline',
                url: `https://${window.location.hostname}/${streamPath}/`,
                client_id: String(item.client_id),
                agent_id: String(item.agent_id)
            };

            setLiveStreamCamera(cameraObj);
            setLiveStreamModalOpen(true);
        } catch (error: any) {
            showError("Failed to decode token or generate stream URL");
            console.error(error);
        }
    };

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const [prevInitialStatus, setPrevInitialStatus] = useState(initialStatus);

    if (isOpen !== prevIsOpen || initialStatus !== prevInitialStatus) {
        setPrevIsOpen(isOpen);
        setPrevInitialStatus(initialStatus);
        if (isOpen) {
            setCameraListStatus(initialStatus);
            setCameraListPage(1);
            setCameraListSearch("");
        }
    }

    const fetchCameraList = useCallback(async (page = 1, search = "") => {
        setCameraListLoading(true);
        try {
            const queryObj: any = {
                asset_type: "camera"
            };
            if (cameraListStatus !== "all") {
                queryObj.status = cameraListStatus;
            }
            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;
            if (locationIds.siteId && locationIds.siteId !== "all") {
                queryObj.client_id = locationIds.siteId;
            } else if (level?.id) {
                queryObj.client_id = Number(level.id);
            }

            console.log("locationIds", locationIds)

            if (search.trim()) {
                queryObj.$or = [
                    { name: { "$ilike": `%${search}%` } },
                    { ip_address: { "$ilike": `%${search}%` } },
                    { hostname: { "$ilike": `%${search}%` } }
                ];
            }

            const response = await baseapi.get("/assets", {
                params: {
                    query: JSON.stringify(queryObj),
                    skip: (page - 1) * cameraListLimit,
                    limit: cameraListLimit,
                    sort_by: "last_seen",
                    sort_order: "desc"
                }
            });

            if (response.data?.status) {
                setCameraList(response.data.data || []);
                setCameraListTotal(response.data.total || 0);
            } else {
                setCameraList([]);
                setCameraListTotal(0);
            }
        } catch (error) {
            console.error("Failed to fetch camera list:", error);
            setCameraList([]);
            setCameraListTotal(0);
        } finally {
            setCameraListLoading(false);
        }
    }, [cameraListStatus, locationIds, level, cameraListLimit]);

    useEffect(() => {
        if (isOpen) {
            fetchCameraList(cameraListPage, cameraListSearch);
        }
    }, [isOpen, cameraListStatus, cameraListPage, cameraListSearch, fetchCameraList]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
                    />

                    {/* Side panel */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-[500px] bg-white border-l border-slate-200 z-[101] flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-[#422568]/10 text-[#422568]">
                                    <Cctv className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest leading-none">
                                        {cameraListStatus === "all" ? "All" : cameraListStatus === "active" ? "Online" : "Offline"} Cameras
                                    </h2>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">
                                        Surveillance asset connectivity details
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-h-0 flex flex-col p-1">
                            {/* Status Tabs Selector inside sidebar */}
                            <div className="flex border-b border-slate-100 mb-4 text-[10px] font-black uppercase tracking-wider gap-4">
                                <button
                                    onClick={() => {
                                        setCameraListStatus("all");
                                        setCameraListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all ${cameraListStatus === "all"
                                        ? "border-[#422568] text-[#422568]"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => {
                                        setCameraListStatus("active");
                                        setCameraListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all flex items-center gap-1 ${cameraListStatus === "active"
                                        ? "border-emerald-500 text-emerald-600"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Online
                                </button>
                                <button
                                    onClick={() => {
                                        setCameraListStatus("offline");
                                        setCameraListPage(1);
                                    }}
                                    className={`pb-2 border-b-2 transition-all flex items-center gap-1 ${cameraListStatus === "offline"
                                        ? "border-rose-500 text-rose-600"
                                        : "border-transparent text-slate-400 hover:text-slate-600"
                                        }`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                                    Offline
                                </button>
                            </div>

                            {/* Search Input */}
                            <div className="relative mb-4 shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search by name, IP, or hostname..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568]/15 focus:border-[#422568] transition-all"
                                    value={cameraListSearch}
                                    onChange={(e) => {
                                        setCameraListSearch(e.target.value);
                                        setCameraListPage(1);
                                    }}
                                />
                                {cameraListSearch && (
                                    <button
                                        onClick={() => {
                                            setCameraListSearch("");
                                            setCameraListPage(1);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-200"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Camera Cards List */}
                            <div className="flex-1 overflow-y-auto custom-scroll pr-1 space-y-2 mb-2">
                                {cameraListLoading ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20">
                                        <div className="w-8 h-8 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin mb-3" />
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                                            Loading Cameras...
                                        </span>
                                    </div>
                                ) : cameraList.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20 text-slate-400 opacity-60">
                                        <Cctv className="w-10 h-10 mb-2.5" />
                                        <p className="text-[10px] font-black uppercase tracking-wider">No Cameras Found</p>
                                    </div>
                                ) : (
                                    cameraList.map((cam: any) => (
                                        <div
                                            key={cam.id}
                                            className="bg-white border border-slate-200 hover:border-[#422568]/30 rounded-xl p-3 flex flex-col gap-1.5 transition-all shadow-sm hover:shadow group"
                                        >
                                            {/* Top row: Name & Status */}
                                            <div className="flex justify-between items-start gap-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1 bg-[#422568]/5 text-[#422568] rounded-lg">
                                                        <Cctv className="w-3 h-3" />
                                                    </div>
                                                    <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-tight group-hover:text-[#422568] transition-colors truncate max-w-[240px]">
                                                        {cam.name || "Unnamed Camera"}
                                                    </h4>
                                                </div>
                                                <span
                                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border shadow-sm shrink-0 ${cam.status === "active"
                                                        ? "bg-emerald-50 border-emerald-250 text-emerald-700"
                                                        : "bg-rose-50 border-rose-250 text-rose-700"
                                                        }`}
                                                >
                                                    <span
                                                        className={`h-1 w-1 rounded-full ${cam.status === "active" ? "bg-emerald-500" : "bg-rose-500 animate-pulse"
                                                            }`}
                                                    />
                                                    {cam.status === "active" ? "Online" : "Offline"}
                                                </span>
                                            </div>

                                            {cam.status !== "active" && cam.last_offline_at && (
                                                <div className="text-[10px] text-rose-700 bg-rose-50/50 border border-rose-100 rounded-lg px-2.5 py-1 font-mono flex items-center justify-between">
                                                    <span className="font-bold text-[8px] font-sans text-rose-500 uppercase tracking-wider">Offline Since</span>
                                                    <span className="font-bold">{formatDate(cam.last_offline_at)}</span>
                                                </div>
                                            )}

                                            {/* Single Line Detail for Hostname, and buttons for Last Frame & Live Stream */}
                                            <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono leading-none pt-1.5 border-t border-slate-100/60">
                                                <div className="flex items-center gap-1.5 truncate">
                                                    <span className="font-bold text-slate-700 bg-slate-100 px-1 py-0.5 rounded text-[8px] font-sans">HOST</span>
                                                    <span className="text-slate-700 truncate max-w-[150px]" title={cam.hostname}>{cam.hostname || "--"}</span>
                                                </div>
                                                {cam.client_name && (
                                                    <span className="text-[9px] font-bold text-slate-800 uppercase tracking-tight truncate max-w-[240px]" title={cam.client_name}>
                                                        {cam.client_name}
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <button
                                                        onClick={() => fetchLastFrame(cam.client_id, cam.id, cam.name)}
                                                        className="p-1 text-slate-400 hover:text-white hover:bg-[#422568] rounded transition-all"
                                                        title="View Last Frame"
                                                    >
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenLiveStream(cam)}
                                                        disabled={!(cam.status?.toLowerCase() === 'active' || cam.status?.toLowerCase() === 'online')}
                                                        className={`p-1 rounded transition-all ${(cam.status?.toLowerCase() === 'active' || cam.status?.toLowerCase() === 'online')
                                                            ? 'text-emerald-500 hover:text-white hover:bg-emerald-600'
                                                            : 'text-slate-300 cursor-not-allowed'
                                                            }`}
                                                        title="View Live Stream"
                                                    >
                                                        <Play className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {cameraListTotal > cameraListLimit && (
                                <div className="shrink-0 pt-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-xl px-2 py-1.5">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        Page {cameraListPage} of {Math.ceil(cameraListTotal / cameraListLimit)}
                                    </span>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => setCameraListPage((p) => Math.max(1, p - 1))}
                                            disabled={cameraListPage === 1}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            onClick={() =>
                                                setCameraListPage((p) =>
                                                    Math.min(Math.ceil(cameraListTotal / cameraListLimit), p + 1)
                                                )
                                            }
                                            disabled={cameraListPage >= Math.ceil(cameraListTotal / cameraListLimit)}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* Last Frame Modal */}
                    {lastFrameModalOpen && createPortal(
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
                            <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden animate-in zoom-in-95 duration-200">
                                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                                    <div className="flex items-center gap-4">
                                        <div className="p-2 bg-emerald-50 rounded-xl shadow-sm border border-emerald-100"><Eye className="w-5 h-5 text-emerald-600" /></div>
                                        <div>
                                            <h3 className="text-xs font-black uppercase tracking-[0.1em] text-slate-900">{lastFrameAssetName}</h3>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5 flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                Visual Identity Feed
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={() => setLastFrameModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-all group">
                                        <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                                    </button>
                                </div>
                                <div className="p-4 bg-slate-100/30">
                                    {lastFrameLoading ? (
                                        <div className="h-[50vh] flex flex-col items-center justify-center text-slate-400 gap-4 bg-white rounded-2xl border border-dashed border-slate-200 shadow-inner">
                                            <Loader2 className="w-10 h-10 animate-spin text-[#422568]" />
                                            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Synchronizing Visual Data...</span>
                                        </div>
                                    ) : lastFrameImage ? (
                                        <div className="space-y-4">
                                            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-black shadow-2xl ring-1 ring-white/10 group">
                                                <img
                                                    src={`data:image/jpeg;base64,${lastFrameImage}`}
                                                    alt="last frame"
                                                    className="w-full h-auto object-contain max-h-[70vh] mx-auto transition-transform duration-500 group-hover:scale-[1.01]"
                                                />
                                                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-[50vh] flex flex-col items-center justify-center text-slate-400 gap-4 bg-white rounded-2xl border border-dashed border-slate-200 shadow-inner">
                                            <div className="p-4 bg-rose-50 rounded-full"><AlertTriangle className="w-10 h-10 text-rose-500" /></div>
                                            <div className="text-center">
                                                <span className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-900 block mb-1">Visual Stream Unavailable</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Verify camera credentials and connectivity</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    <LiveStreamModal
                        open={liveStreamModalOpen}
                        onClose={() => { setLiveStreamModalOpen(false); setLiveStreamCamera(null); }}
                        camera={liveStreamCamera}
                    />
                </>
            )}
        </AnimatePresence>
    );
};
